import { supabase } from "@/lib/supabase";
import type { SectionDetail } from "@/components/cleaner/SectionDetailSheet";
import { areCleanerShellQueryParamsReady } from "@/lib/cleanerQueryGuards";
import { isStaffAssignedToLocation } from "@/lib/cleanerAssignedLocations";
import { isAssignmentPlaceholderSection } from "@/lib/locationAccess";
import { hasActiveWorkSession } from "@/lib/cleanerWorkSession";
import { cleanerActiveTasksOrFilter } from "@/lib/cleanerTaskQuery";

export interface CleanerSectionShellData {
  section: SectionDetail;
  propertyAddress: string;
  taskIds: string[];
  isWorkStarted: boolean;
}

export async function fetchCleanerSectionShell(
  propertyId: string,
  sectionId: string,
  orgId: string
): Promise<CleanerSectionShellData> {
  if (!sectionId || sectionId === "undefined" || sectionId === "null") {
    throw new Error("Zablokowano zapytanie z błędnym ID (undefined).");
  }
  if (!areCleanerShellQueryParamsReady(propertyId, sectionId, orgId)) {
    throw new Error("[fetchCleanerSectionShell] Invalid property, section, or org id");
  }

  const [{ data: authData }, locationResult, sectionResult] = await Promise.all([
    supabase.auth.getUser(),
    supabase
      .from("cleaning_locations")
      .select("address, status, instruction_notes, access_code")
      .eq("id", propertyId)
      .eq("org_id", orgId)
      .eq("is_cleaning_active", true)
      .maybeSingle(),
    supabase
      .from("property_sections")
      .select("id, name, assigned_staff_id, allow_cross_access")
      .eq("id", sectionId)
      .eq("location_id", propertyId)
      .maybeSingle(),
  ]);

  if (locationResult.error) {
    throw locationResult.error;
  }
  if (sectionResult.error) {
    throw sectionResult.error;
  }

  const currentUserId = authData.user?.id ?? null;
  const locationData = locationResult.data;
  const sectionData = sectionResult.data;
  if (!locationData) {
    throw new Error("[fetchCleanerSectionShell] Location not found");
  }
  if (!sectionData) {
    throw new Error("[fetchCleanerSectionShell] Section not found");
  }
  if (isAssignmentPlaceholderSection(sectionData.name)) {
    throw new Error("Nie masz przypisania do tej nieruchomości.");
  }
  if (!currentUserId) {
    throw new Error("Wymagane logowanie.");
  }

  const assignedToLocation = await isStaffAssignedToLocation(currentUserId, propertyId);
  if (!assignedToLocation) {
    throw new Error("Nie masz przypisania do tej nieruchomości.");
  }

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayStartISO = todayStart.toISOString();

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const todayEndISO = todayEnd.toISOString();

  const { data: tasksRowsData, error: tasksError } = await supabase
    .from("cleaning_tasks")
    .select("id")
    .eq("location_id", propertyId)
    .eq("org_id", orgId)
    .neq("status", "cancelled")
    .or(cleanerActiveTasksOrFilter(todayStartISO, todayEndISO));
  if (tasksError) {
    throw tasksError;
  }
  const tasksRows = tasksRowsData ?? [];
  const taskIds = tasksRows.map((task) => task.id);

  const isWorkStarted = hasActiveWorkSession(propertyId);

  return {
    section: {
      id: sectionData.id,
      name: sectionData.name,
      assignedTo: undefined,
      isAssignedToMe:
        !sectionData.assigned_staff_id || sectionData.assigned_staff_id === currentUserId,
      allowCrossAccess: sectionData.allow_cross_access ?? true,
      checklistTasks: [],
      activityLog: [],
      instructionNotes: locationData.instruction_notes || undefined,
      accessCode: locationData.access_code || undefined,
    },
    propertyAddress: locationData.address || "",
    taskIds,
    isWorkStarted,
  };
}
